/**
 * NotificationEmail - Where a partner's offer update emails go.
 *
 * Empty means the API user's own email. Kept apart from that account's public
 * email, which listing agents see on the offer.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { getErrorMessage } from '@/common/utils/errors'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Input } from '@/common/components/input'
import { updatePartner, type PartnerItem } from '../../api/partners'

interface NotificationEmailProps {
  partner: PartnerItem
}

export function NotificationEmail({ partner }: NotificationEmailProps) {
  const { token } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()
  const queryClient = useQueryClient()
  // Null until someone edits the field, so a refreshed partner shows through
  // instead of a stale copy that Save would write back.
  const [draft, setDraft] = useState<string | null>(null)
  const savedEmail = partner.notification_email ?? ''
  const accountEmail = partner.default_notification_email
  const fallback = accountEmail ? `the API user's email, ${accountEmail}` : "the API user's email"

  const save = useMutation({
    mutationFn: (notificationEmail: string | null) =>
      updatePartner(token ?? '', partner.id, { notification_email: notificationEmail }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['admin', 'partners', partner.id], updated)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'partners'] })
      setDraft(null)
      showSuccessToast(
        updated.notification_email
          ? `Offer updates now go to ${updated.notification_email}.`
          : "Offer updates now go to the API user's email.",
      )
    },
    onError: (error: unknown) => showErrorToast(getErrorMessage(error)),
  })

  const trimmed = (draft ?? savedEmail).trim()
  const isChanged = draft !== null && trimmed.toLowerCase() !== savedEmail

  return (
    <Stack gap="8">
      <Text as="h2" variant="bodyLg" weight="bold">
        Email for offer updates
      </Text>
      <Text variant="bodySm" color="muted">
        {partner.display_name} is emailed here when a listing agent engages, counters, accepts or rejects one of
        its offers. Leave it empty to use {fallback}.
      </Text>
      <Text variant="bodySm">
        Offer updates currently go to {partner.notification_email ?? accountEmail ?? "the API user's email"}.
      </Text>
      <Stack direction="row" gap="12" align="end" wrap>
        <Input
          label="Notification email"
          type="email"
          value={draft ?? savedEmail}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={accountEmail ?? undefined}
          disabled={save.isPending}
          optional
        />
        <Button onClick={() => save.mutate(trimmed || null)} disabled={!isChanged || save.isPending}>
          Save notification email
        </Button>
      </Stack>
    </Stack>
  )
}
